import React, { useState, useEffect } from "react";
import { 
  Search, 
  ShoppingBag, 
  X, 
  Plus, 
  Minus, 
  CheckCircle, 
  Mail, 
  Phone, 
  MessageCircle, 
  Loader2 
} from "lucide-react";
import "./App.css";

const API_BASE_URL = "https://ratelistapi.rmproject.in";
// const API_BASE_URL = "http://localhost:5001";

function App() {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=500&auto=format&fit=crop&q=60";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // States
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    address: ""
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState(null);
  const [shopDetails, setShopDetails] = useState({
    shopName: "Wine Shop",
    description: "hamare pass sabhi tarah ki whisky, rum, beer available hai",
    email: "vikramsingh60237509@gmail.com",
    phone: "9116916320",
    whatsapp: "+919602375098"
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch products and shop details on mount
  useEffect(() => {
    fetchProducts();
    fetchShopDetails();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/products`);
      if (!response.ok) {
        throw new Error("Failed to load products from server");
      }
      const data = await response.json();
      setProducts(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Unable to connect to the backend server. Please make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  };

  const fetchShopDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shop-details`);
      if (response.ok) {
        const data = await response.json();
        setShopDetails(data);
        setSettingsLoaded(true);
      }
    } catch (err) {
      console.error("Failed to load shop details:", err);
    }
  };

  // Cart Handlers
  const addToCart = (product) => {
    setCart((prevCart) => {
      const prodId = product._id || product.id;
      const existing = prevCart.find((item) => (item.product._id || item.product.id) === prodId);
      if (existing) {
        return prevCart.map((item) =>
          (item.product._id || item.product.id) === prodId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart((prevCart) => {
      return prevCart
        .map((item) => {
          if ((item.product._id || item.product.id) === productId) {
            const newQty = item.quantity + delta;
            return { ...item, quantity: newQty };
          }
          return item;
        })
        .filter((item) => item.quantity > 0);
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => (item.product._id || item.product.id) !== productId));
  };

  // Get current item count & total amount in cart
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotalAmount = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  // Dynamic calculations from admin settings
  const discountAmount = Number(shopDetails.discount) || 0;
  const deliveryCharge = Number(shopDetails.deliveryCharge) || 0;
  const toPayAmount = Math.max(0, cartTotalAmount - discountAmount + deliveryCharge);

  // Get quantity of a product currently in cart
  const getProductQuantity = (productId) => {
    const item = cart.find((item) => (item.product._id || item.product.id) === productId);
    return item ? item.quantity : 0;
  };

  // Filter Categories
  const categories = ["All", ...new Set(products.map((p) => p.category))];

  // Filter Products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handle Form Change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCheckoutForm((prev) => ({ ...prev, [name]: value }));
  };

  // Check if Form is valid for checkout
  const isFormValid = 
    settingsLoaded &&
    checkoutForm.name.trim() !== "" &&
    checkoutForm.phone.trim().length >= 10 &&
    checkoutForm.address.trim() !== "";

  // Place Order
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!isFormValid || cart.length === 0) return;

    try {
      setSubmitting(true);
      const cleanPhone = checkoutForm.phone.trim();
      const fullPhone = `${checkoutForm.countryCode}${cleanPhone}`;

      const orderData = {
        customerName: checkoutForm.name.trim(),
        customerPhone: fullPhone,
        customerAddress: checkoutForm.address.trim(),
        items: cart.map((item) => ({
          productId: item.product._id || item.product.id,
          name: item.product.name,
          price: item.product.price,
          quantity: item.quantity
        })),
        totalAmount: toPayAmount
      };

      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error("Failed to place the order");
      }

      const data = await response.json();
      setOrderResult(data);
      
      // Reset checkout form and cart
      setCart([]);
      setCheckoutForm({
        name: "",
        countryCode: "+91",
        phone: "",
        address: ""
      });
      setCartOpen(false);
    } catch (err) {
      console.error(err);
      alert("Something went wrong while placing your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle WhatsApp Redirection
  const handleOpenWhatsApp = () => {
    if (orderResult && orderResult.adminWhatsAppUrl) {
      window.open(orderResult.adminWhatsAppUrl, "_blank");
    }
  };

  const handleCloseSuccessModal = () => {
    setOrderResult(null);
  };

  return (
    <div className="app-container">
      {/* Header Banner */}
      <header className="shop-header">
        <div className="header-info">
          <h1 className="shop-title">{shopDetails.shopName || "Wine Shop"}</h1>
          <p className="shop-description">
            {shopDetails.description || "hamare pass sabhi tarah ki whisky, rum, beer available hai"}
          </p>
          <div className="contact-info">
            {/* <a href={`mailto:${shopDetails.email || "vikramsingh60237509@gmail.com"}`} className="contact-badge">
              <Mail />
              <span>{shopDetails.email || "vikramsingh60237509@gmail.com"}</span>
            </a> */}
            <a 
              href={`tel:${shopDetails.phone || "9116916320"}`} 
              className="contact-badge"
              onClick={(e) => {
                e.preventDefault();
                const whatsappNum = (shopDetails.whatsapp || "+919602375098").replace(/\D/g, "");
                const phoneNum = (shopDetails.phone || "9116916320").replace(/\D/g, "");
                
                const whatsappUrl = `whatsapp://send?phone=${whatsappNum}`;
                const dialerUrl = `tel:${phoneNum}`;
                
                const start = Date.now();
                window.location.href = whatsappUrl;
                
                setTimeout(() => {
                  if (!document.hidden && Date.now() - start < 1500) {
                    window.location.href = dialerUrl;
                  }
                }, 1000);
              }}
            >
              <Phone />
              {/* <span>{shopDetails.phone || "9116916320"}</span> */}
              <span>{"WhatsApp"}</span>
            </a>
          </div>
        </div>
        <div className="shop-logo-container">
          {shopDetails.image ? (
            <img 
              src={getImageUrl(shopDetails.image)} 
              alt={shopDetails.shopName || "Shop Logo"} 
              className="shop-logo-img"
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", border: "3px solid white", boxShadow: "0 4px 10px rgba(0,0,0,0.15)" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=500&auto=format&fit=crop&q=60";
              }}
            />
          ) : (
            <div className="shop-logo-circle">
              🏪
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      {error && (
        <div style={{
          background: "#ffebeb", 
          border: "1px solid #ffd0d0", 
          color: "#c0392b", 
          padding: "16px", 
          borderRadius: "12px", 
          marginBottom: "24px",
          fontWeight: "500"
        }}>
          {error}
          <button 
            onClick={fetchProducts} 
            style={{
              marginLeft: "12px", 
              padding: "6px 12px", 
              background: "#c0392b", 
              color: "white", 
              border: "none", 
              borderRadius: "6px", 
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="search-filter-section">
        <div className="search-bar-wrapper">
          <Search />
          <input
            type="text"
            className="search-input"
            placeholder="Search for an item"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category Pills */}
        <div className="categories-container">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-pill ${selectedCategory === category ? "active" : ""}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px", gap: "10px" }}>
          <Loader2 className="animate-spin" style={{ width: "32px", height: "32px", color: "var(--primary)" }} />
          <span style={{ fontWeight: "600", color: "var(--text-muted)" }}>Loading catalog...</span>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 16px", color: "var(--text-muted)" }}>
          <ShoppingBag style={{ width: "48px", height: "48px", strokeWidth: 1.5, margin: "0 auto 12px auto" }} />
          <h3>No items found</h3>
          <p>Try resetting filters or searching for something else.</p>
        </div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map((product) => {
            const prodId = product._id || product.id;
            const qty = getProductQuantity(prodId);
            return (
              <div key={prodId} className="product-card">
                <div className="product-image-wrapper">
                  <span className="product-category-tag">{product.category}</span>
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=500&auto=format&fit=crop&q=60";
                    }}
                  />
                </div>
                <div className="product-details">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description || "Fresh premium collection."}</p>
                  <div className="product-action-row">
                    <span className="product-price">₹{product.price}</span>
                    {qty === 0 ? (
                      <button className="add-btn" onClick={() => addToCart(product)}>
                        +Add
                      </button>
                    ) : (
                      <div className="quantity-control">
                        <button className="qty-btn" onClick={() => updateQuantity(prodId, -1)}>
                          <Minus size={14} />
                        </button>
                        <span className="qty-value">{qty}</span>
                        <button className="qty-btn" onClick={() => addToCart(product)}>
                          <Plus size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Bottom Cart Bar */}
      {cartItemCount > 0 && (
        <div className="bottom-cart-bar" onClick={() => setCartOpen(true)}>
          <div className="cart-bar-left">
            <span>{cartItemCount} {cartItemCount === 1 ? "ITEM" : "ITEMS"}</span>
            <span>•</span>
            <span>₹{toPayAmount.toFixed(2)}</span>
          </div>
          <div className="cart-bar-right">
            <ShoppingBag />
            <span>View cart</span>
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      <div className={`drawer-overlay ${cartOpen ? "open" : ""}`} onClick={() => setCartOpen(false)}>
        <div className="cart-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="drawer-header">
            <h2 className="drawer-title">Shopping Cart</h2>
            <button className="close-btn" onClick={() => setCartOpen(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="drawer-content">
            {/* Order Summary */}
            <div className="drawer-section">
              <div className="section-title">
                <span>Order Summary</span>
                <span className="section-item-count">{cartItemCount} items</span>
              </div>
              {cart.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Your cart is empty.</p>
              ) : (
                <div className="summary-items-list">
                  {cart.map((item) => {
                    const prodId = item.product._id || item.product.id;
                    return (
                      <div key={prodId} className="summary-item">
                        <img
                          src={getImageUrl(item.product.image)}
                          alt={item.product.name}
                          className="summary-item-img"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=500&auto=format&fit=crop&q=60";
                          }}
                        />
                        <div className="summary-item-info">
                          <h4 className="summary-item-name">{item.product.name}</h4>
                          <div className="summary-item-price-qty">
                            <span>₹{item.product.price}</span>
                            <span>x</span>
                            <div className="quantity-control" style={{ display: "inline-flex", transform: "scale(0.85)", margin: "-4px 0" }}>
                              <button className="qty-btn" onClick={() => updateQuantity(prodId, -1)}>
                                <Minus size={12} />
                              </button>
                              <span className="qty-value">{item.quantity}</span>
                              <button className="qty-btn" onClick={() => addToCart(item.product)}>
                                <Plus size={12} />
                              </button>
                            </div>
                          </div>
                          <button className="summary-remove-btn" onClick={() => removeFromCart(prodId)}>
                            Remove
                          </button>
                        </div>
                        <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                          ₹{(item.product.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Delivery Address Form */}
            <div className="drawer-section">
              <h3 className="section-title">Delivery Address</h3>
              <form onSubmit={handlePlaceOrder}>
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    placeholder="Enter your name"
                    value={checkoutForm.name}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <div className="phone-input-container">
                    <select
                      name="countryCode"
                      className="country-code-select"
                      value={checkoutForm.countryCode}
                      onChange={handleFormChange}
                    >
                      <option value="+91">+91 (IN)</option>
                      <option value="+1">+1 (US)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+971">+971 (UAE)</option>
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-input"
                      placeholder="10-digit number"
                      value={checkoutForm.phone}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <textarea
                    id="address"
                    name="address"
                    className="form-input form-textarea"
                    placeholder="Enter full delivery address"
                    value={checkoutForm.address}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </form>
            </div>

            {/* Price Details */}
            <div className="drawer-section">
              <h3 className="section-title">Price Details</h3>
              <div className="price-details-table">
                <div className="price-row">
                  <span>Sub Total</span>
                  <span>₹{cartTotalAmount.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>Discounts</span>
                  <span className="discount-value">- ₹{discountAmount.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>Delivery/Other Charges</span>
                  <span className={deliveryCharge > 0 ? "" : "discount-value"}>
                    {deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : "FREE"}
                  </span>
                </div>
                <div className="price-row total">
                  <span>To Pay</span>
                  <span>₹{toPayAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Place Order Drawer Footer */}
          <div className="drawer-footer">
            <button
              className={`place-order-btn ${isFormValid && cart.length > 0 ? "active" : ""}`}
              disabled={!isFormValid || cart.length === 0 || submitting}
              onClick={handlePlaceOrder}
            >
              {submitting ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Placing Order...</span>
                </div>
              ) : (
                `Place Order (₹${toPayAmount.toFixed(2)})`
              )}
            </button>
            <span className="cod-label">COD (Cash On Delivery)</span>
          </div>
        </div>
      </div>

      {/* Order Success Modal */}
      {orderResult && (
        <div className="success-overlay">
          <div className="success-card">
            <div className="success-icon-wrapper">
              <CheckCircle />
            </div>
            <h2 className="success-title">Order Placed Successfully!</h2>
            <p className="success-description">
              Thank you, <strong>{orderResult.order.customerName}</strong>. Your order is registered with ID: <strong>{orderResult.order.id}</strong>.
            </p>

            <div className="whatsapp-simulated-badge">
              <strong>📢 WhatsApp Notification Status:</strong>
              <p>✓ Automated messages logged in backend. (Check terminal output or `whatsapp_logs.json`).</p>
            </div>

            <button className="whatsapp-btn" onClick={handleOpenWhatsApp}>
              <MessageCircle />
              Send Order Details on WhatsApp
            </button>

            <button className="secondary-action-btn" onClick={handleCloseSuccessModal}>
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
